import { Typography } from 'antd'
import Card from 'components/card/Card'
import { IconArrowLeft } from 'components/icons'
import { rupiahFormat } from 'helpers/formatter'
import OrderStatusLabel from 'components/order/OrderStatusLabel'
import {
  AdminsOrdersDeliveriesUpdateRequest,
  DeliveryEnum,
  OrdersListOperationResponseItem,
  SaleOrderStatusEnum,
} from 'generated/api'
import DataCard, { IList } from 'components/card/DataCard'
import ModalConfirmation from './ModalConfirmation'
import ModalChangePickUp from './ModalChangePickUp'

interface PropsOrderDetail {
  isLoading: boolean
  isLoadingModal: boolean
  isShowConfirmPopup: boolean
  isShowChangePickUpPopup: boolean
  selectedOrder: OrdersListOperationResponseItem
  selectedPaymentDetail: () => IList[]
  selectedDeliveryDetail: () => IList[]
  selectedRecipientDetail: () => IList[]
  onSelectedOrder: (order: OrdersListOperationResponseItem | null) => void
  isOrderAllowedChangePickup: () => boolean
  openConfirmOrderModal: () => void
  openChangePickUpModal: () => void
  cancelConfirmOrderModal: () => void
  cancelChangePickUpModal: () => void
  acceptConfirmOrderModal: () => void
  acceptChangePickUpModal: (value: AdminsOrdersDeliveriesUpdateRequest) => void
}

export default function OrderDetail(props: PropsOrderDetail) {
  const { selectedOrder } = props

  return (
    <Card loading={props.isLoading} dataTestId="card_orders_detail">
      <Card.Header className="mt-0">
        <button
          data-testid="btn_back_to_list"
          className="mr-4 bg-transparent border-0 cursor-pointer"
          onClick={() => props.onSelectedOrder(null)}
        >
          {<IconArrowLeft />}
        </button>
        <Typography.Title className="m-0 text-2xl">Detail Pesanan</Typography.Title>
      </Card.Header>
      <Card.Body>
        <div className="md:flex md:justify-between md:items-start mb-6">
          <div className="mb-6 md:mb-0">
            <Typography.Title className="mt-0 text-lg font-semibold">
              Nomor Pesanan :
              <span className="ml-3 text-xl font-bold text-amber-500">
                {selectedOrder.orderNo ? selectedOrder.orderNo : '-'}
              </span>
            </Typography.Title>
            <Typography.Title className="mt-0 text-lg font-semibold">
              Nama Pemesan :
              <span className="ml-3 text-xl font-bold">
                {selectedOrder.userName ? selectedOrder.userName : '-'}
              </span>
            </Typography.Title>
            <Typography.Title className="mt-0 text-lg font-semibold">
              Total Tagihan :
              <span className="ml-3 text-xl font-bold">
                {selectedOrder?.totalItemPrice ? rupiahFormat(selectedOrder?.totalItemPrice) : '-'}
              </span>
            </Typography.Title>
            {selectedOrder?.status ? <OrderStatusLabel status={selectedOrder?.status} /> : null}
          </div>
          <div className="flex flex-col gap-3">
            {(selectedOrder.status === SaleOrderStatusEnum.New ||
              selectedOrder.status === SaleOrderStatusEnum.Confirmed ||
              selectedOrder.status === SaleOrderStatusEnum.Delivering) &&
            selectedOrder.orderItems.length >= 1 ? (
              <div>
                <button
                  data-testid="btn_confirm_order"
                  onClick={props.openConfirmOrderModal}
                  className="py-4 px-6 bg-primary border-0 rounded-lg text-white font-bold cursor-pointer hover:bg-primary-700 text-sm w-full"
                >
                  {selectedOrder.status === SaleOrderStatusEnum.New ? 'KONFIRMASI PESANAN' : null}
                  {selectedOrder.status === SaleOrderStatusEnum.Confirmed &&
                  selectedOrder.orderDelivery.method === DeliveryEnum.Pickup
                    ? 'PESANAN SIAP DI AMBIL'
                    : null}
                  {selectedOrder.status === SaleOrderStatusEnum.Confirmed &&
                  selectedOrder.orderDelivery.method === DeliveryEnum.Delivery
                    ? 'PESANAN DALAM PENGIRIMAN'
                    : null}
                  {selectedOrder.status === SaleOrderStatusEnum.Delivering &&
                  selectedOrder.orderDelivery.method === DeliveryEnum.Delivery
                    ? 'PESANAN TELAH DITERIMA'
                    : null}
                </button>
              </div>
            ) : null}
            {props.isOrderAllowedChangePickup() ? (
              <div>
                <button
                  data-testid="btn_change_pick_up"
                  onClick={props.openChangePickUpModal}
                  className="py-4 px-6 bg-white border border-primary rounded-lg font-bold cursor-pointer text-sm text-primary uppercase w-full"
                >
                  ubah detail
                </button>
              </div>
            ) : null}
          </div>
        </div>
        <div className="flex flex-col md:flex-row gap-2 flex-wrap mb-6">
          <DataCard title="Detail Pembayaran" list={props.selectedPaymentDetail()} />
          <DataCard title="Informasi Pengiriman" list={props.selectedDeliveryDetail()} />
          <DataCard title="Detail Penjemput" list={props.selectedRecipientDetail()} />
        </div>
        {selectedOrder.orderItems.length >= 1 ? (
          <>
            <Typography.Text className="mb-2 font-bold">Daftar Pesanan</Typography.Text>;
            <table className="w-full mt-2 table-auto">
              <thead>
                <tr>
                  <th className="py-2 pl-0 pr-4 text-sm text-left">Info Produk</th>
                  <th className="px-4 py-2 text-sm text-left">Jumlah</th>
                  <th className="px-4 py-2 text-sm text-left">Harga Satuan</th>
                  <th className="py-2 pl-4 pr-0 text-sm text-left">Total</th>
                </tr>
              </thead>
              <tbody>
                {selectedOrder.orderItems.map(item => (
                  <tr key={item.id}>
                    <td className="py-2 pl-0 pr-4 text-sm border">{item.productName}</td>
                    <td className="px-4 py-2 text-sm border">{item.quantity}</td>
                    <td className="px-4 py-2 text-sm border">{rupiahFormat(item.productPrice)}</td>
                    <td className="py-2 pl-4 pr-0 text-sm border">
                      {rupiahFormat(item.quantity * item.productPrice)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : null}

        <ModalConfirmation
          loading={props.isLoadingModal}
          visibility={props.isShowConfirmPopup}
          orderStatus={selectedOrder ? selectedOrder.status : null}
          deliveryMethod={selectedOrder ? selectedOrder.orderDelivery.method : null}
          onCancel={props.cancelConfirmOrderModal}
          onAccept={props.acceptConfirmOrderModal}
        />
        <ModalChangePickUp
          initialValue={{
            recipientName: selectedOrder.orderDelivery?.recipientName || '',
            recipientPhoneNo: selectedOrder.orderDelivery?.recipientPhoneNo || '',
            vehicleLicenseNo: selectedOrder.orderDelivery?.vehicleLicenseNo || '',
          }}
          loading={props.isLoadingModal}
          visibility={props.isShowChangePickUpPopup}
          onCancel={props.cancelChangePickUpModal}
          onAccept={values => props.acceptChangePickUpModal(values)}
        />
      </Card.Body>
    </Card>
  )
}
